<template>
  <IonApp>
    <template v-if="olderChromeVersion">
      <div style="font-size: 24px; padding: 4px 0">
        <p>
          Устаревшая версия WebView {{ chromeVersion }}.
          <br />
          Пожалуйста, обновите версию минимум до 89.
        </p>
        <p>
          <Button
            @click="openUpdateWebview"
            :label="'Обновить в Google Play'"
          />
        </p>
      </div>
    </template>
    <template v-else>
      <IonRouterOutlet id="main" />
      <MenusChat />
      <Menus />
      <ProgressVideoUpload />
      <ModalYandexCaptcha
        :show="showModalYandexCaptcha"
        @onHide="showModalYandexCaptcha = false"
      />
    </template>
  </IonApp>
</template>

<script>
import {
  alertController,
  IonApp,
  IonRouterOutlet,
  menuController,
  useIonRouter,
  useBackButton,
  isPlatform
} from '@ionic/vue';
import { Capacitor } from '@capacitor/core';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import Menus from '@/components/Menus.vue';
import ModalYandexCaptcha from './components/captcha/ModalYandexCaptcha.vue';

import MenusChat from './components/chat/Menus.vue';
import { pushNotifications } from './helpers/pushNotifications';
import ProgressVideoUpload from './components/videos/ProgressVideoUpload.vue';
import { App as CapacitorApp } from '@capacitor/app';
import { AnalyticsService } from '@/plugins/appmetrica';
import { tnSetup } from '@/../vendors/webchat/src/lib/tinode';
import { parseUrl } from './helpers/web';
import { openNewWindow } from './helpers/openNewWindow';
import Button from './components/ui/Button.vue';
import { resolveDeferredDeepLink } from '@/helpers/deferredDeepLink';

const normalizeDeferredTargetUrl = (url) => {
  if (!url) {
    return null;
  }

  try {
    const parsedUrl = url.startsWith('http')
      ? new URL(url)
      : new URL(url, 'https://mymove.ru');

    if (/^\/company\/[^/]+/.test(parsedUrl.pathname)) {
      parsedUrl.searchParams.set(
        'open_from',
        parsedUrl.searchParams.get('open_from') || 'deferred'
      );
    }

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export default {
  name: 'App',
  components: {
    IonApp,
    IonRouterOutlet,
    Menus,
    MenusChat,
    ProgressVideoUpload,
    ModalYandexCaptcha,
    Button
  },
  computed: {
    ...mapGetters('user', ['hasSession', 'session', 'user']),

    ...mapGetters(['error', 'lang'])
  },
  data: () => ({
    showModalYandexCaptcha: false,
    olderChromeVersion: false,
    chromeVersion: null,
    handlingUnauthorized: false,
    initialSessionRestore: true,
    suppressUnauthorizedUntil: Date.now() + 5000,
    deferredTargetOpened: false
  }),
  watch: {
    error(newError, prevError) {
      if (newError && newError !== prevError) {
        if (typeof newError === 'string') {
          this.presentAlert();
        } else if (typeof newError === 'object') {
          if (newError?.type === 'Unauthorized') {
            if (this.handlingUnauthorized) {
              return;
            }
            const isPublicRoute = this.isPublicRoute(this.$route);
            const suppressUnauthorizedToast =
              this.initialSessionRestore ||
              Date.now() < this.suppressUnauthorizedUntil ||
              isPublicRoute;
            this.handlingUnauthorized = true;
            if (!suppressUnauthorizedToast) {
              this.$toast(this.$t('PLEASE-LOGIN'), 4000);
            }
            this.logout()
              .then(() => {
                if (!suppressUnauthorizedToast) {
                  this.$router.replace('/');
                }
              })
              .finally(() => {
                this.clearError();
                this.handlingUnauthorized = false;
                this.initialSessionRestore = false;
              });
          }
          if (newError?.type === 'Network') {
            const { method } = newError;
            const message =
              method === 'post' || method === 'delete' || method === 'put'
                ? this.$t('ERROR-SEND-DATA-INTERNET')
                : this.$t('ERROR-GET-DATA-INTERNET');
            this.$toast(message, 4000);
          }
          if (newError?.type === 'YandexCaptcha') {
            this.showModalYandexCaptcha = true;
            this.$toast('Пожалуйста, подтвердите, что вы не робот.', 4000);
          }
        }
      }
    },
    lang: {
      immediate: true,
      handler(newLang, oldLang) {
        if (newLang && newLang !== oldLang) {
          this.$i18n.locale = newLang;
          this.$moment.locale(newLang);
        }
      }
    },
    hasSession: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          if (this.hasSession) {
            const requestPushPermission = oldVal === false;
            pushNotifications.OnInit(this.session?.idSession, {
              requestPushPermission
            });
            this.getUser()
              .then((user) => {
                this.loadAccess().catch((e) => {
                  console.error(e);
                });
                pushNotifications.subscribeTo(user.id, {
                  registerDevice: requestPushPermission
                });
                if (
                  !isPlatform('desktop') &&
                  !user.firstname &&
                  process.env.VUE_APP_FROM_PROJECT == 'center'
                ) {
                  this.$router.push({
                    name: this.$ROUTES.routeProfileEdit().name,
                    hash: '#welcomeMoedvizhenie'
                  });
                }
              })
              .catch((e) => {
                console.error(e);
              })
              .finally(() => {
                this.initialSessionRestore = false;
              });

            this.getChatCabinets().then((cabinets) => {
              cabinets?.forEach((c) => {
                this.connectToChat(c?.access, c?.id);
              });
            });
          }
        } else if (oldVal === undefined) {
          this.initialSessionRestore = false;
        }
      }
    }
  },
  mounted() {
    let isReallyExit = false;
    const ionRouter = useIonRouter();
    menuController.enable(true, 'profile-menu');
    menuController.enable(true, 'menu-chat-cabinets');
    useBackButton(-1, () => {
      if (!ionRouter.canGoBack() && !document.location.hash) {
        if (isReallyExit) {
          CapacitorApp.exitApp();
        } else {
          this.$toast('Нажмите еще раз для выхода', 1000, 'top');
          isReallyExit = true;
          setTimeout(() => {
            isReallyExit = false;
          }, 1000);
        }
      }
    });

    CapacitorApp.addListener('appUrlOpen', (data) => {
      try {
        const url = data?.url;
        if (url) {
          if (/mymove\.ru\/meet\/(.+)$/.test(url)) {
            openNewWindow(url);
          } else {
            const routeData = parseUrl(url);
            if (routeData) {
              this.deferredTargetOpened = true;
              this.$router.push(routeData.route);
            }
          }
        }
      } catch (e) {
        console.error(e);
      }
    });

    setTimeout(() => {
      this.openDeferredTarget();
    }, 800);
  },
  created() {
    this.analytics = new AnalyticsService();
    this.analytics.initialization();
    this.checkOlderBrowser();
    window.addEventListener('message', (event) => {
      if (
        typeof event.data == 'object' &&
        event.data?.event == 'auth_by_token' &&
        event.data?.token
      ) {
        this.SET_ID_SESSION(event.data?.token);
        this.SET_TYPE('user');
      }
    });
  },
  methods: {
    ...mapActions(['logout']),
    ...mapActions('user', ['getUser']),
    ...mapActions('access', ['loadAccess']),
    ...mapActions('chat', ['setUnread', 'getChatCabinets']),
    ...mapMutations('user', ['SET_ID_SESSION', 'SET_TYPE']),
    async openDeferredTarget() {
      try {
        if (this.deferredTargetOpened) {
          return;
        }

        await this.$router.isReady?.();
        const data = await resolveDeferredDeepLink();
        if (data?.matched && data?.target_url) {
          const targetUrl = normalizeDeferredTargetUrl(data.target_url);
          if (targetUrl) {
            this.deferredTargetOpened = true;
            this.$router.push(targetUrl);
          }
        }
      } catch (e) {
        console.error(e);
      }
    },
    openUpdateWebview() {
      openNewWindow(
        'https://play.google.com/store/apps/details?id=com.google.android.webview'
      );
    },
    onContactUpdate() {},
    connectToChat(chatAccess, cabinetId) {
      const token = chatAccess;
      const tn = tnSetup(
        process.env.VUE_APP_CHAT_HOST,
        process.env.VUE_APP_CHAT_SEC === 'true',
        'def',
        'ru',
        true,
        () => {
          // token.expires = new Date(token?.expires);

          tn.connect()
            .then(() => {})
            .catch((err) => {
              console.error(err);
            });
        },
        Capacitor.getPlatform(),
        true,
        process.env.VUE_APP_CHAT_HOST + token?.id
      );
      tn.registerCallback('onConnect', () => {
        tn.autoLoginToken(token?.token, {}).then(() => {
          const me = tn.getMeTopic();
          me.registerCallback('onContactUpdate', () => {
            this.updateChatContacts(me, cabinetId);
          });
          me.registerCallback('onSubsUpdated', () => {
            this.updateChatContacts(me, cabinetId);
          });
          me.subscribe(
            me
              .startMetaQuery()
              .withLaterSub()
              .build()
          ).then(() => {
            this.updateChatContacts(me, cabinetId);
          });
        });
      });
    },
    updateChatContacts(me, cabinetId) {
      let unread = 0;
      me.contacts((c) => {
        unread += c?.unread ?? 0;
      });
      this.setUnread({ unread, cabinetId });
    },
    async presentAlert() {
      try {
        const alert = await alertController.create({
          message: this.error,
          buttons: [this.$t('OK')]
        });
        await alert.present();
        await alert.onDidDismiss();
        this.clearError();
      } catch (e) {
        console.error(e);
      }
    },
    clearError() {
      window.$store.dispatch('setError', null);
    },
    isPublicRoute(route) {
      const path = route?.path ?? '';
      return /^\/company\/[^/]+/.test(path);
    },
    onTokenExpired(data) {
      console.log('onTokenExpired', data);
    },
    onSuccessCatcha(data) {
      console.log('onSuccessCatcha', data);
    },
    checkOlderBrowser() {
      const userAgent = window?.navigator?.userAgent;
      if (userAgent) {
        const chromeMatch = userAgent.match(/(Chrome|Chromium)\/(\d+)/);

        if (!chromeMatch) return;

        const majorVersion = parseInt(chromeMatch[2], 10);

        if (majorVersion < 89) {
          this.chromeVersion = majorVersion;
          this.olderChromeVersion = true;
        }
      }
    }
  }
};
</script>

<style lang="scss">
@import './theme/main.scss';
</style>
